import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import type { CharacterResult as CharacterResultType, CharacterChangeInfo } from '@creanizer/types'
import { Copy, Download, Save, Edit } from 'lucide-react'
import { useState } from 'react'
import { CharacterEditModal } from './CharacterEditModal'

interface CharacterResultProps {
  result: CharacterResultType
  onSave?: () => void
  isSaved?: boolean
  onEdit?: (updatedResult: Partial<CharacterResultType>) => void
}

// ハイライト用のヘルパー関数
function useChangeHighlight(changeInfo: CharacterChangeInfo | undefined) {
  const isUpdated = (fieldPath: string): boolean => {
    return changeInfo?.updatedFields?.includes(fieldPath) || false
  }

  const isAdded = (fieldPath: string): boolean => {
    return changeInfo?.addedFields?.includes(fieldPath) || false
  }

  const isDialogueAdded = (index: number): boolean => {
    return changeInfo?.addedDialogueIndices?.includes(index) || false
  }

  const isDialogueUpdated = (index: number): boolean => {
    return changeInfo?.updatedFields?.includes(`sampleDialogues.${index}`) || false
  }

  const getFieldClassName = (fieldPath: string): string => {
    if (isAdded(fieldPath)) return 'bg-green-50 dark:bg-green-950 border-l-4 border-green-500 pl-2'
    if (isUpdated(fieldPath)) return 'bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-500 pl-2'
    return ''
  }

  const getFieldBadge = (fieldPath: string): React.ReactNode => {
    if (isAdded(fieldPath)) {
      return <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">追加</span>
    }
    if (isUpdated(fieldPath)) {
      return <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded">更新</span>
    }
    return null
  }

  return { isUpdated, isAdded, isDialogueAdded, isDialogueUpdated, getFieldClassName, getFieldBadge }
}

export function CharacterResult({ result, onSave, isSaved, onEdit }: CharacterResultProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const { isUpdated, isAdded, isDialogueAdded, isDialogueUpdated, getFieldClassName, getFieldBadge } = useChangeHighlight(result._changeInfo)
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const renderArrayItem = (item: any): string => {
    if (typeof item === 'string') return item
    if (typeof item === 'object' && item !== null) {
      if (item.name && item.relation) return `${item.name} - ${item.relation}`
      if (item.event && item.description) return `${item.event}: ${item.description}`
      if (item.character && item.relationship) return `${item.character} - ${item.relationship}`
      return JSON.stringify(item)
    }
    return String(item)
  }

  const downloadAsText = () => {
    const content = formatCharacterAsText(result)
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `character_${result.name || 'unknown'}_${Date.now()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card">
      <div className="mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold">{result.name || 'データなし'}</h2>
            <p className="text-muted-foreground">
              {result.age || 'データなし'} / {result.gender || 'データなし'} / {result.role || 'データなし'}
            </p>
          </div>
          <div className="flex gap-2">
            {onSave && (
              <Button size="sm" variant="outline" onClick={onSave} disabled={isSaved}>
                <Save className="h-4 w-4 mr-1" />
                {isSaved ? '保存済み' : '保存'}
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="outline" onClick={() => setIsEditModalOpen(true)}>
                <Edit className="h-4 w-4 mr-1" />
                編集
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={downloadAsText}>
              <Download className="h-4 w-4 mr-1" />
              ダウンロード
            </Button>
          </div>
        </div>
      </div>
      
      <div>
        <div className="space-y-6">
          <div className={getFieldClassName('summary')}>
            <h3 className="font-semibold mb-2">概要{getFieldBadge('summary')}</h3>
            <p className="text-muted-foreground">{result.summary || 'データなし'}</p>
          </div>

          <Tabs defaultValue="personality" className="w-full">
            <TabsList className="flex flex-wrap gap-1 w-full h-auto">
              <TabsTrigger value="personality">性格</TabsTrigger>
              <TabsTrigger value="background">背景</TabsTrigger>
              <TabsTrigger value="appearance">外見</TabsTrigger>
              <TabsTrigger value="behavior">行動</TabsTrigger>
              <TabsTrigger value="development">成長</TabsTrigger>
              <TabsTrigger value="emotional">感情</TabsTrigger>
              <TabsTrigger value="interpersonal">対人</TabsTrigger>
              <TabsTrigger value="thinking">思考</TabsTrigger>
              <TabsTrigger value="daily">日常</TabsTrigger>
              <TabsTrigger value="beliefs">信念</TabsTrigger>
              <TabsTrigger value="expertise">専門</TabsTrigger>
              <TabsTrigger value="relations">関係詳細</TabsTrigger>
            </TabsList>

            <TabsContent value="personality" className="space-y-4">
              <div className={getFieldClassName('personality.traits')}>
                <h4 className="font-medium mb-1">性格特性{getFieldBadge('personality.traits')}</h4>
                <div className="flex flex-wrap gap-2">
                  {result.personality?.traits?.map((trait, i) => (
                    <span key={i} className="px-2 py-1 bg-primary/10 rounded text-sm">{renderArrayItem(trait)}</span>
                  )) || <span className="text-muted-foreground">データなし</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('personality.strengths')}>
                  <h4 className="font-medium mb-1">長所{getFieldBadge('personality.strengths')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.strengths?.map((s, i) => (
                      <li key={i}>・{renderArrayItem(s)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                <div className={getFieldClassName('personality.weaknesses')}>
                  <h4 className="font-medium mb-1">短所・弱点{getFieldBadge('personality.weaknesses')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.weaknesses?.map((w, i) => (
                      <li key={i}>・{renderArrayItem(w)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className={getFieldClassName('personality.fears')}>
                  <h4 className="font-medium mb-1">恐れ{getFieldBadge('personality.fears')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.fears?.map((f, i) => (
                      <li key={i}>・{renderArrayItem(f)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                <div className={getFieldClassName('personality.desires')}>
                  <h4 className="font-medium mb-1">欲求{getFieldBadge('personality.desires')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.desires?.map((d, i) => (
                      <li key={i}>・{renderArrayItem(d)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                <div className={getFieldClassName('personality.values')}>
                  <h4 className="font-medium mb-1">価値観{getFieldBadge('personality.values')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.values?.map((v, i) => (
                      <li key={i}>・{renderArrayItem(v)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className={getFieldClassName('personality.likes')}>
                  <h4 className="font-medium mb-1">好き{getFieldBadge('personality.likes')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.likes?.map((l, i) => (
                      <li key={i}>・{renderArrayItem(l)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                <div className={getFieldClassName('personality.dislikes')}>
                  <h4 className="font-medium mb-1">嫌い{getFieldBadge('personality.dislikes')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.dislikes?.map((d, i) => (
                      <li key={i}>・{renderArrayItem(d)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                <div className={getFieldClassName('personality.goals')}>
                  <h4 className="font-medium mb-1">目標{getFieldBadge('personality.goals')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.personality?.goals?.map((g, i) => (
                      <li key={i}>・{renderArrayItem(g)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="background" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('background.species')}>
                  <h4 className="font-medium mb-1">種族・民族{getFieldBadge('background.species')}</h4>
                  <p className="text-sm text-muted-foreground">{result.background?.species || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('background.language')}>
                  <h4 className="font-medium mb-1">言語・方言{getFieldBadge('background.language')}</h4>
                  <p className="text-sm text-muted-foreground">{result.background?.language || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('background.birthplace')}>
                  <h4 className="font-medium mb-1">出身地{getFieldBadge('background.birthplace')}</h4>
                  <p className="text-sm text-muted-foreground">{result.background?.birthplace || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('background.occupation')}>
                  <h4 className="font-medium mb-1">職業・立場{getFieldBadge('background.occupation')}</h4>
                  <p className="text-sm text-muted-foreground">{result.background?.occupation || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('background.family')}>
                  <h4 className="font-medium mb-1">家族構成{getFieldBadge('background.family')}</h4>
                  <p className="text-sm text-muted-foreground">{result.background?.family || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('background.education')}>
                  <h4 className="font-medium mb-1">教育・訓練{getFieldBadge('background.education')}</h4>
                  <p className="text-sm text-muted-foreground">{result.background?.education || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('background.significantEvents')}>
                  <h4 className="font-medium mb-1">重要な出来事{getFieldBadge('background.significantEvents')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.background?.significantEvents?.map((e, i) => (
                      <li key={i}>・{renderArrayItem(e)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                <div className={getFieldClassName('background.relationships')}>
                  <h4 className="font-medium mb-1">人間関係{getFieldBadge('background.relationships')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.background?.relationships?.map((r, i) => (
                      <li key={i}>・{renderArrayItem(r)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('background.abilities')}>
                  <h4 className="font-medium mb-1">能力・スキル{getFieldBadge('background.abilities')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.background?.abilities?.map((a, i) => (
                      <li key={i}>・{renderArrayItem(a)}</li>
                    )) || <li>データなし</li>}
                  </ul>
                </div>
                {result.background?.secrets && result.background.secrets.length > 0 && (
                  <div className={getFieldClassName('background.secrets')}>
                    <h4 className="font-medium mb-1">秘密{getFieldBadge('background.secrets')}</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {result.background.secrets.map((s, i) => (
                        <li key={i}>・{renderArrayItem(s)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {result.background?.backstoryNarrative && (
                <div className={getFieldClassName('background.backstoryNarrative')}>
                  <h4 className="font-medium mb-1">バックストーリー{getFieldBadge('background.backstoryNarrative')}</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{result.background.backstoryNarrative}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="appearance" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('appearance.height')}>
                  <h4 className="font-medium mb-1">身長{getFieldBadge('appearance.height')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.height || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('appearance.build')}>
                  <h4 className="font-medium mb-1">体格{getFieldBadge('appearance.build')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.build || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`${getFieldClassName('appearance.hairColor')} ${getFieldClassName('appearance.hairStyle')}`}>
                  <h4 className="font-medium mb-1">髪{getFieldBadge('appearance.hairColor')}{getFieldBadge('appearance.hairStyle')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.hairColor || 'データなし'} / {result.appearance?.hairStyle || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('appearance.eyeColor')}>
                  <h4 className="font-medium mb-1">瞳の色{getFieldBadge('appearance.eyeColor')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.eyeColor || 'データなし'}</p>
                </div>
              </div>
              <div className={getFieldClassName('appearance.facialFeatures')}>
                <h4 className="font-medium mb-1">顔立ち{getFieldBadge('appearance.facialFeatures')}</h4>
                <p className="text-sm text-muted-foreground">{result.appearance?.facialFeatures || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('appearance.clothing')}>
                <h4 className="font-medium mb-1">服装{getFieldBadge('appearance.clothing')}</h4>
                <p className="text-sm text-muted-foreground">{result.appearance?.clothing || 'データなし'}</p>
              </div>
              {result.appearance?.distinctiveMarks && (
                <div className={getFieldClassName('appearance.distinctiveMarks')}>
                  <h4 className="font-medium mb-1">特徴的な印{getFieldBadge('appearance.distinctiveMarks')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance.distinctiveMarks}</p>
                </div>
              )}
              <div className={getFieldClassName('appearance.overallImpression')}>
                <h4 className="font-medium mb-1">全体的な印象{getFieldBadge('appearance.overallImpression')}</h4>
                <p className="text-sm text-muted-foreground">{result.appearance?.overallImpression || 'データなし'}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className={getFieldClassName('appearance.weight')}>
                  <h4 className="font-medium mb-1">体重{getFieldBadge('appearance.weight')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.weight || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('appearance.voiceQuality')}>
                  <h4 className="font-medium mb-1">声質{getFieldBadge('appearance.voiceQuality')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.voiceQuality || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('appearance.posture')}>
                  <h4 className="font-medium mb-1">姿勢{getFieldBadge('appearance.posture')}</h4>
                  <p className="text-sm text-muted-foreground">{result.appearance?.posture || 'データなし'}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="behavior" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('behavior.firstPerson')}>
                  <h4 className="font-medium mb-1">一人称{getFieldBadge('behavior.firstPerson')}</h4>
                  <p className="text-sm text-muted-foreground font-semibold text-base">「{result.behavior?.firstPerson || '私'}」</p>
                </div>
                <div className={getFieldClassName('behavior.speechPattern')}>
                  <h4 className="font-medium mb-1">話し方{getFieldBadge('behavior.speechPattern')}</h4>
                  <p className="text-sm text-muted-foreground">{result.behavior?.speechPattern || 'データなし'}</p>
                </div>
              </div>
              <div className={getFieldClassName('behavior.mannerisms')}>
                <h4 className="font-medium mb-1">癖・仕草{getFieldBadge('behavior.mannerisms')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.behavior?.mannerisms?.map((m, i) => (
                    <li key={i}>・{renderArrayItem(m)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className={getFieldClassName('behavior.habits')}>
                <h4 className="font-medium mb-1">習慣{getFieldBadge('behavior.habits')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.behavior?.habits?.map((h, i) => (
                    <li key={i}>・{renderArrayItem(h)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('behavior.socialStyle')}>
                  <h4 className="font-medium mb-1">対人スタイル{getFieldBadge('behavior.socialStyle')}</h4>
                  <p className="text-sm text-muted-foreground">{result.behavior?.socialStyle || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('behavior.decisionMaking')}>
                  <h4 className="font-medium mb-1">意思決定{getFieldBadge('behavior.decisionMaking')}</h4>
                  <p className="text-sm text-muted-foreground">{result.behavior?.decisionMaking || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('behavior.stressResponse')}>
                  <h4 className="font-medium mb-1">ストレス反応{getFieldBadge('behavior.stressResponse')}</h4>
                  <p className="text-sm text-muted-foreground">{result.behavior?.stressResponse || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('behavior.conflictResolution')}>
                  <h4 className="font-medium mb-1">対立への対処{getFieldBadge('behavior.conflictResolution')}</h4>
                  <p className="text-sm text-muted-foreground">{result.behavior?.conflictResolution || 'データなし'}</p>
                </div>
              </div>
              <div className={getFieldClassName('behavior.combatStyle')}>
                <h4 className="font-medium mb-1">武器・戦闘スタイル{getFieldBadge('behavior.combatStyle')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.behavior?.combatStyle?.map((c, i) => (
                    <li key={i}>・{renderArrayItem(c)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className={getFieldClassName('behavior.catchphrases')}>
                <h4 className="font-medium mb-1">口癖{getFieldBadge('behavior.catchphrases')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.behavior?.catchphrases?.map((c, i) => (
                    <li key={i}>・{renderArrayItem(c)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="development" className="space-y-4">
              <div className={getFieldClassName('development.currentState')}>
                <h4 className="font-medium mb-1">現在の状態{getFieldBadge('development.currentState')}</h4>
                <p className="text-sm text-muted-foreground">{result.development?.currentState || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('development.growthPotential')}>
                <h4 className="font-medium mb-1">成長の可能性{getFieldBadge('development.growthPotential')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.development?.growthPotential?.map((g, i) => (
                    <li key={i}>・{renderArrayItem(g)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className={getFieldClassName('development.internalConflicts')}>
                <h4 className="font-medium mb-1">内的葛藤{getFieldBadge('development.internalConflicts')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.development?.internalConflicts?.map((c, i) => (
                    <li key={i}>・{renderArrayItem(c)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className={getFieldClassName('development.externalChallenges')}>
                <h4 className="font-medium mb-1">外的課題{getFieldBadge('development.externalChallenges')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.development?.externalChallenges?.map((c, i) => (
                    <li key={i}>・{renderArrayItem(c)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className={getFieldClassName('development.arcSuggestions')}>
                <h4 className="font-medium mb-1">物語の展開案{getFieldBadge('development.arcSuggestions')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.development?.arcSuggestions?.map((a, i) => (
                    <li key={i}>・{renderArrayItem(a)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="emotional" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('emotionalExpression.joy')}>
                  <h4 className="font-medium mb-1">喜び{getFieldBadge('emotionalExpression.joy')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.joy || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('emotionalExpression.anger')}>
                  <h4 className="font-medium mb-1">怒り{getFieldBadge('emotionalExpression.anger')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.anger || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('emotionalExpression.sadness')}>
                  <h4 className="font-medium mb-1">哀しみ{getFieldBadge('emotionalExpression.sadness')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.sadness || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('emotionalExpression.enjoyment')}>
                  <h4 className="font-medium mb-1">楽しみ{getFieldBadge('emotionalExpression.enjoyment')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.enjoyment || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('emotionalExpression.emotionalControl')}>
                  <h4 className="font-medium mb-1">感情制御{getFieldBadge('emotionalExpression.emotionalControl')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.emotionalControl || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('emotionalExpression.stressRelief')}>
                  <h4 className="font-medium mb-1">ストレス発散{getFieldBadge('emotionalExpression.stressRelief')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.stressRelief || 'データなし'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('emotionalExpression.cryingStyle')}>
                  <h4 className="font-medium mb-1">泣き方{getFieldBadge('emotionalExpression.cryingStyle')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.cryingStyle || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('emotionalExpression.laughingStyle')}>
                  <h4 className="font-medium mb-1">笑い方{getFieldBadge('emotionalExpression.laughingStyle')}</h4>
                  <p className="text-sm text-muted-foreground">{result.emotionalExpression?.laughingStyle || 'データなし'}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="interpersonal" className="space-y-4">
              <div className={getFieldClassName('interpersonalPatterns.firstMeetingBehavior')}>
                <h4 className="font-medium mb-1">初対面での接し方{getFieldBadge('interpersonalPatterns.firstMeetingBehavior')}</h4>
                <p className="text-sm text-muted-foreground">{result.interpersonalPatterns?.firstMeetingBehavior || 'データなし'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('interpersonalPatterns.trustCriteria')}>
                  <h4 className="font-medium mb-1">信頼基準{getFieldBadge('interpersonalPatterns.trustCriteria')}</h4>
                  <p className="text-sm text-muted-foreground">{result.interpersonalPatterns?.trustCriteria || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('interpersonalPatterns.distanceCriteria')}>
                  <h4 className="font-medium mb-1">距離を置く基準{getFieldBadge('interpersonalPatterns.distanceCriteria')}</h4>
                  <p className="text-sm text-muted-foreground">{result.interpersonalPatterns?.distanceCriteria || 'データなし'}</p>
                </div>
              </div>
              <div className={getFieldClassName('interpersonalPatterns.romanticView')}>
                <h4 className="font-medium mb-1">恋愛観{getFieldBadge('interpersonalPatterns.romanticView')}</h4>
                <p className="text-sm text-muted-foreground">{result.interpersonalPatterns?.romanticView || 'データなし'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('interpersonalPatterns.hierarchyBehavior')}>
                  <h4 className="font-medium mb-1">上下関係での振る舞い{getFieldBadge('interpersonalPatterns.hierarchyBehavior')}</h4>
                  <p className="text-sm text-muted-foreground">{result.interpersonalPatterns?.hierarchyBehavior || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('interpersonalPatterns.groupPosition')}>
                  <h4 className="font-medium mb-1">集団での立ち位置{getFieldBadge('interpersonalPatterns.groupPosition')}</h4>
                  <p className="text-sm text-muted-foreground">{result.interpersonalPatterns?.groupPosition || 'データなし'}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="thinking" className="space-y-4">
              <div className={getFieldClassName('thinkingProcess.problemSolvingApproach')}>
                <h4 className="font-medium mb-1">問題解決アプローチ{getFieldBadge('thinkingProcess.problemSolvingApproach')}</h4>
                <p className="text-sm text-muted-foreground">{result.thinkingProcess?.problemSolvingApproach || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('thinkingProcess.crisisJudgment')}>
                <h4 className="font-medium mb-1">危機時の判断{getFieldBadge('thinkingProcess.crisisJudgment')}</h4>
                <p className="text-sm text-muted-foreground">{result.thinkingProcess?.crisisJudgment || 'データなし'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={getFieldClassName('thinkingProcess.prioritization')}>
                  <h4 className="font-medium mb-1">優先順位の付け方{getFieldBadge('thinkingProcess.prioritization')}</h4>
                  <p className="text-sm text-muted-foreground">{result.thinkingProcess?.prioritization || 'データなし'}</p>
                </div>
                <div className={getFieldClassName('thinkingProcess.planningAbility')}>
                  <h4 className="font-medium mb-1">計画性{getFieldBadge('thinkingProcess.planningAbility')}</h4>
                  <p className="text-sm text-muted-foreground">{result.thinkingProcess?.planningAbility || 'データなし'}</p>
                </div>
              </div>
              <div className={getFieldClassName('thinkingProcess.intuitionLogicBalance')}>
                <h4 className="font-medium mb-1">直感と論理のバランス{getFieldBadge('thinkingProcess.intuitionLogicBalance')}</h4>
                <p className="text-sm text-muted-foreground">{result.thinkingProcess?.intuitionLogicBalance || 'データなし'}</p>
              </div>
            </TabsContent>

            <TabsContent value="daily" className="space-y-4">
              <div className={getFieldClassName('dailyLife.routine')}>
                <h4 className="font-medium mb-1">日常のルーティン{getFieldBadge('dailyLife.routine')}</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{result.dailyLife?.routine || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('dailyLife.importantItems')}>
                <h4 className="font-medium mb-1">大切なアイテム{getFieldBadge('dailyLife.importantItems')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.dailyLife?.importantItems?.map((item, i) => (
                    <li key={i}>・{renderArrayItem(item)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="beliefs" className="space-y-4">
              <div className={getFieldClassName('beliefs.lifePhilosophy')}>
                <h4 className="font-medium mb-1">人生哲学・座右の銘{getFieldBadge('beliefs.lifePhilosophy')}</h4>
                <p className="text-sm text-muted-foreground">{result.beliefs?.lifePhilosophy || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('beliefs.workBeliefs')}>
                <h4 className="font-medium mb-1">仕事への信念{getFieldBadge('beliefs.workBeliefs')}</h4>
                <p className="text-sm text-muted-foreground">{result.beliefs?.workBeliefs || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('beliefs.worldview')}>
                <h4 className="font-medium mb-1">世界観・社会観{getFieldBadge('beliefs.worldview')}</h4>
                <p className="text-sm text-muted-foreground">{result.beliefs?.worldview || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('beliefs.coreValues')}>
                <h4 className="font-medium mb-1">核となる価値観{getFieldBadge('beliefs.coreValues')}</h4>
                <p className="text-sm text-muted-foreground">{result.beliefs?.coreValues || 'データなし'}</p>
              </div>
              {result.beliefs?.faith && (
                <div className={getFieldClassName('beliefs.faith')}>
                  <h4 className="font-medium mb-1">信仰・思想{getFieldBadge('beliefs.faith')}</h4>
                  <p className="text-sm text-muted-foreground">{result.beliefs.faith}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="expertise" className="space-y-4">
              <div className={getFieldClassName('expertise.mainField')}>
                <h4 className="font-medium mb-1">専門分野{getFieldBadge('expertise.mainField')}</h4>
                <p className="text-sm text-muted-foreground">{result.expertise?.mainField || 'データなし'}</p>
              </div>
              <div className={getFieldClassName('expertise.practicalSkills')}>
                <h4 className="font-medium mb-1">実務能力{getFieldBadge('expertise.practicalSkills')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.expertise?.practicalSkills?.map((skill, i) => (
                    <li key={i}>・{renderArrayItem(skill)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              {result.expertise?.intellectualWorks && (
                <div className={getFieldClassName('expertise.intellectualWorks')}>
                  <h4 className="font-medium mb-1">著書・知的活動{getFieldBadge('expertise.intellectualWorks')}</h4>
                  <p className="text-sm text-muted-foreground">{result.expertise.intellectualWorks}</p>
                </div>
              )}
              <div className={getFieldClassName('expertise.characteristicEpisodes')}>
                <h4 className="font-medium mb-1">キャラクター性を示すエピソード{getFieldBadge('expertise.characteristicEpisodes')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.expertise?.characteristicEpisodes?.map((ep, i) => (
                    <li key={i}>・{renderArrayItem(ep)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="relations" className="space-y-4">
              <div className={getFieldClassName('relationshipDetails.nicknames')}>
                <h4 className="font-medium mb-1">呼び方{getFieldBadge('relationshipDetails.nicknames')}</h4>
                {result.relationshipDetails?.nicknames && Object.keys(result.relationshipDetails.nicknames).length > 0 ? (
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {Object.entries(result.relationshipDetails.nicknames).map(([name, nickname], i) => (
                      <li key={i}>・{name}: 「{nickname}」</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">データなし</p>
                )}
              </div>
              <div className={getFieldClassName('relationshipDetails.respectedPeople')}>
                <h4 className="font-medium mb-1">尊敬する人物{getFieldBadge('relationshipDetails.respectedPeople')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.relationshipDetails?.respectedPeople?.map((person, i) => (
                    <li key={i}>・{renderArrayItem(person)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
              <div className={getFieldClassName('relationshipDetails.rivals')}>
                <h4 className="font-medium mb-1">ライバル{getFieldBadge('relationshipDetails.rivals')}</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.relationshipDetails?.rivals?.map((rival, i) => (
                    <li key={i}>・{renderArrayItem(rival)}</li>
                  )) || <li>データなし</li>}
                </ul>
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <h3 className="font-semibold mb-3">サンプル台詞</h3>
            <div className="space-y-3">
              {result.sampleDialogues?.map((dialogue, i) => (
                dialogue && dialogue.line ? (
                  <div
                    key={i}
                    className={`card p-4 ${
                      isDialogueAdded(i)
                        ? 'border-2 border-green-500 bg-green-50 dark:bg-green-950'
                        : isDialogueUpdated(i)
                          ? 'border-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-950'
                          : ''
                    }`}
                  >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {dialogue.category && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary/20 rounded">{dialogue.category}</span>
                          )}
                          <p className="text-sm text-muted-foreground">{dialogue.situation || ''}</p>
                          {isDialogueAdded(i) && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">NEW</span>
                          )}
                          {isDialogueUpdated(i) && (
                            <span className="px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded">更新</span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(dialogue.line)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="font-medium">
                        {dialogue.line.startsWith('「') && dialogue.line.endsWith('」')
                          ? dialogue.line
                          : `「${dialogue.line}」`}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">（{dialogue.tone || ''}）</p>
                  </div>
                ) : null
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={getFieldClassName('authorNotes')}>
              <h3 className="font-semibold mb-2">作者向けメモ{getFieldBadge('authorNotes')}</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                {result.authorNotes?.map((note, i) => (
                  <li key={i}>・{renderArrayItem(note)}</li>
                )) || <li>データなし</li>}
              </ul>
            </div>
            <div className={getFieldClassName('plotHooks')}>
              <h3 className="font-semibold mb-2">物語への組み込み{getFieldBadge('plotHooks')}</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                {result.plotHooks?.map((hook, i) => (
                  <li key={i}>・{renderArrayItem(hook)}</li>
                )) || <li>データなし</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {onEdit && (
        <CharacterEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={(updatedResult) => {
            onEdit(updatedResult)
            setIsEditModalOpen(false)
          }}
          characterResult={result}
          title="キャラクター設定を編集"
        />
      )}
    </div>
  )
}

function formatCharacterAsText(result: CharacterResultType): string {
  let content = `キャラクター設定
================

【基本情報】
名前: ${result.name || 'データなし'}
年齢: ${result.age || 'データなし'}
性別: ${result.gender || 'データなし'}
役割: ${result.role || 'データなし'}

【概要】
${result.summary || 'データなし'}

【性格】
特性: ${result.personality?.traits?.join(', ') || 'データなし'}
長所: ${result.personality?.strengths?.join(', ') || 'データなし'}
短所: ${result.personality?.weaknesses?.join(', ') || 'データなし'}
恐れ: ${result.personality?.fears?.join(', ') || 'データなし'}
欲求: ${result.personality?.desires?.join(', ') || 'データなし'}
価値観: ${result.personality?.values?.join(', ') || 'データなし'}
`
  if (result.personality?.likes?.length) {
    content += `好きなもの: ${result.personality.likes.join(', ')}\n`
  }
  if (result.personality?.dislikes?.length) {
    content += `嫌いなもの: ${result.personality.dislikes.join(', ')}\n`
  }
  if (result.personality?.goals?.length) {
    content += `目標: ${result.personality.goals.join(', ')}\n`
  }

  content += `
【背景】
`
  if (result.background?.species) {
    content += `種族・民族: ${result.background.species}\n`
  }
  if (result.background?.language) {
    content += `言語・方言: ${result.background.language}\n`
  }
  content += `出身地: ${result.background?.birthplace || 'データなし'}
家族: ${result.background?.family || 'データなし'}
教育: ${result.background?.education || 'データなし'}
職業: ${result.background?.occupation || 'データなし'}
重要な出来事:
${result.background?.significantEvents?.map(e => `  - ${e}`).join('\n') || '  - データなし'}
人間関係:
${result.background?.relationships?.map(r => {
  if (typeof r === 'string') return `  - ${r}`
  if (typeof r === 'object' && r !== null) {
    const name = (r as any).name || (r as any).character || ''
    const relation = (r as any).relation || (r as any).relationship || ''
    return `  - ${name} - ${relation}`
  }
  return `  - ${String(r)}`
}).join('\n') || '  - データなし'}
`
  if (result.background?.secrets && result.background.secrets.length > 0) {
    content += `秘密:\n${result.background.secrets.map(s => `  - ${s}`).join('\n')}\n`
  }
  content += `能力・スキル:
${result.background?.abilities?.map(a => `  - ${a}`).join('\n') || '  - データなし'}
`
  if (result.background?.backstoryNarrative) {
    content += `バックストーリー:\n${result.background.backstoryNarrative}\n`
  }

  content += `
【外見】
身長: ${result.appearance?.height || 'データなし'}
`
  if (result.appearance?.weight) {
    content += `体重: ${result.appearance.weight}\n`
  }
  content += `体格: ${result.appearance?.build || 'データなし'}
髪: ${result.appearance?.hairColor || 'データなし'} / ${result.appearance?.hairStyle || 'データなし'}
瞳: ${result.appearance?.eyeColor || 'データなし'}
顔立ち: ${result.appearance?.facialFeatures || 'データなし'}
服装: ${result.appearance?.clothing || 'データなし'}
`
  if (result.appearance?.distinctiveMarks) {
    content += `特徴: ${result.appearance.distinctiveMarks}\n`
  }
  content += `印象: ${result.appearance?.overallImpression || 'データなし'}
`
  if (result.appearance?.voiceQuality) {
    content += `声質: ${result.appearance.voiceQuality}\n`
  }
  if (result.appearance?.posture) {
    content += `姿勢: ${result.appearance.posture}\n`
  }

  content += `
【行動パターン】
一人称: ${result.behavior?.firstPerson || '私'}
話し方: ${result.behavior?.speechPattern || 'データなし'}
`
  if (result.behavior?.catchphrases?.length) {
    content += `口癖: ${result.behavior.catchphrases.join(', ')}\n`
  }
  content += `癖: ${result.behavior?.mannerisms?.join(', ') || 'データなし'}
習慣: ${result.behavior?.habits?.join(', ') || 'データなし'}
対人スタイル: ${result.behavior?.socialStyle || 'データなし'}
意思決定: ${result.behavior?.decisionMaking || 'データなし'}
ストレス反応: ${result.behavior?.stressResponse || 'データなし'}
対立への対処: ${result.behavior?.conflictResolution || 'データなし'}
武器・戦闘スタイル:
${result.behavior?.combatStyle?.map(c => `  - ${c}`).join('\n') || '  - データなし'}

【成長・発展】
現在の状態: ${result.development?.currentState || 'データなし'}
成長の可能性:
${result.development?.growthPotential?.map(g => `  - ${g}`).join('\n') || '  - データなし'}
内的葛藤:
${result.development?.internalConflicts?.map(c => `  - ${c}`).join('\n') || '  - データなし'}
外的課題:
${result.development?.externalChallenges?.map(c => `  - ${c}`).join('\n') || '  - データなし'}
展開案:
${result.development?.arcSuggestions?.map(a => `  - ${a}`).join('\n') || '  - データなし'}
`

  // 感情表現
  if (result.emotionalExpression) {
    content += `\n【感情表現】\n`
    if (result.emotionalExpression.joy) content += `喜び: ${result.emotionalExpression.joy}\n`
    if (result.emotionalExpression.anger) content += `怒り: ${result.emotionalExpression.anger}\n`
    if (result.emotionalExpression.sadness) content += `哀しみ: ${result.emotionalExpression.sadness}\n`
    if (result.emotionalExpression.enjoyment) content += `楽しみ: ${result.emotionalExpression.enjoyment}\n`
    if (result.emotionalExpression.emotionalControl) content += `感情抑制: ${result.emotionalExpression.emotionalControl}\n`
    if (result.emotionalExpression.stressRelief) content += `ストレス発散: ${result.emotionalExpression.stressRelief}\n`
    if (result.emotionalExpression.cryingStyle) content += `泣き方: ${result.emotionalExpression.cryingStyle}\n`
    if (result.emotionalExpression.laughingStyle) content += `笑い方: ${result.emotionalExpression.laughingStyle}\n`
  }

  // 対人関係パターン
  if (result.interpersonalPatterns) {
    content += `\n【対人関係パターン】\n`
    if (result.interpersonalPatterns.firstMeetingBehavior) content += `初対面: ${result.interpersonalPatterns.firstMeetingBehavior}\n`
    if (result.interpersonalPatterns.trustCriteria) content += `信頼基準: ${result.interpersonalPatterns.trustCriteria}\n`
    if (result.interpersonalPatterns.distanceCriteria) content += `距離を置く基準: ${result.interpersonalPatterns.distanceCriteria}\n`
    if (result.interpersonalPatterns.romanticView) content += `恋愛観: ${result.interpersonalPatterns.romanticView}\n`
    if (result.interpersonalPatterns.hierarchyBehavior) content += `上下関係: ${result.interpersonalPatterns.hierarchyBehavior}\n`
    if (result.interpersonalPatterns.groupPosition) content += `集団での立場: ${result.interpersonalPatterns.groupPosition}\n`
  }

  // 思考プロセス
  if (result.thinkingProcess) {
    content += `\n【思考プロセス】\n`
    if (result.thinkingProcess.problemSolvingApproach) content += `問題解決: ${result.thinkingProcess.problemSolvingApproach}\n`
    if (result.thinkingProcess.crisisJudgment) content += `危機判断: ${result.thinkingProcess.crisisJudgment}\n`
    if (result.thinkingProcess.prioritization) content += `優先順位: ${result.thinkingProcess.prioritization}\n`
    if (result.thinkingProcess.planningAbility) content += `計画性: ${result.thinkingProcess.planningAbility}\n`
    if (result.thinkingProcess.intuitionLogicBalance) content += `直感/論理: ${result.thinkingProcess.intuitionLogicBalance}\n`
  }

  // 日常生活
  if (result.dailyLife) {
    content += `\n【日常生活】\n`
    if (result.dailyLife.routine) content += `日課: ${result.dailyLife.routine}\n`
    if (result.dailyLife.importantItems?.length) content += `大切なもの: ${result.dailyLife.importantItems.join(', ')}\n`
  }

  // 信念
  if (result.beliefs) {
    content += `\n【信念・哲学】\n`
    if (result.beliefs.lifePhilosophy) content += `人生哲学: ${result.beliefs.lifePhilosophy}\n`
    if (result.beliefs.workBeliefs) content += `仕事への信念: ${result.beliefs.workBeliefs}\n`
    if (result.beliefs.worldview) content += `世界観: ${result.beliefs.worldview}\n`
    if (result.beliefs.coreValues) content += `価値観: ${result.beliefs.coreValues}\n`
    if (result.beliefs.faith) content += `信仰: ${result.beliefs.faith}\n`
  }

  // 専門性
  if (result.expertise) {
    content += `\n【専門性】\n`
    if (result.expertise.mainField) content += `専門分野: ${result.expertise.mainField}\n`
    if (result.expertise.practicalSkills?.length) content += `実務能力: ${result.expertise.practicalSkills.join(', ')}\n`
    if (result.expertise.intellectualWorks) content += `知的活動: ${result.expertise.intellectualWorks}\n`
    if (result.expertise.characteristicEpisodes?.length) {
      content += `エピソード:\n`
      result.expertise.characteristicEpisodes.forEach(e => content += `  - ${e}\n`)
    }
  }

  // 関係性詳細
  if (result.relationshipDetails) {
    content += `\n【関係性詳細】\n`
    if (result.relationshipDetails.nicknames && Object.keys(result.relationshipDetails.nicknames).length > 0) {
      content += `呼び方:\n`
      Object.entries(result.relationshipDetails.nicknames).forEach(([name, nickname]) => {
        content += `  - ${name}: ${nickname}\n`
      })
    }
    if (result.relationshipDetails.respectedPeople?.length) content += `尊敬する人: ${result.relationshipDetails.respectedPeople.join(', ')}\n`
    if (result.relationshipDetails.rivals?.length) content += `ライバル: ${result.relationshipDetails.rivals.join(', ')}\n`
  }

  // サンプル台詞
  content += `\n【サンプル台詞】\n`
  if (result.sampleDialogues && result.sampleDialogues.length > 0) {
    result.sampleDialogues.forEach((d, i) => {
      if (!d || !d.line) return
      const lineWithBrackets = d.line.startsWith('「') && d.line.endsWith('」')
        ? d.line
        : `「${d.line}」`
      content += `${i + 1}. ${d.situation || 'データなし'}${d.category ? ` [${d.category}]` : ''}\n`
      content += `   ${lineWithBrackets}\n`
      content += `   （${d.tone || 'データなし'}）\n`
    })
  } else {
    content += 'データなし\n'
  }

  content += `
【作者向けメモ】
${result.authorNotes?.map(n => `  - ${n}`).join('\n') || '  - データなし'}

【物語への組み込み】
${result.plotHooks?.map(h => `  - ${h}`).join('\n') || '  - データなし'}
`
  if (result.customContext) {
    content += `\n【カスタムコンテキスト】\n${result.customContext}\n`
  }

  content += `
生成日時: ${result.metadata?.generatedAt || new Date().toISOString()}
`
  if (result.metadata?.seed) {
    content += `シード値: ${result.metadata.seed}\n`
  }

  return content
}